import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  Event,
  EventEdition,
  getEvent,
  getAdjacentEditions
} from '../services/eventEditionService';
import { getRegistrationsByUserId, countActiveParticipants, countWaitingList } from '../services/registrationService';
import { getVerboseName } from '../services/codeListService';
import { Globe, Facebook, Trophy, BarChart3, ArrowLeft } from 'lucide-react';
import { getKrultraUrl } from '../config/urls';
import { getEventLogoUrl } from '../services/strapiService';
import { useEventEdition } from '../contexts/EventEditionContext';

const EVENT_ID = 'mo';

const MOOverviewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setEvent: setSelectedEvent } = useEventEdition();

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return t('editions.tba');
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(i18n.language === 'no' ? 'nb-NO' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const COURSE_FACTS = [
    { titleKey: 'mo.classes', descKey: 'mo.classesDesc' },
    { titleKey: 'mo.since2011', descKey: 'mo.since2011Desc' }
  ];

  const REGISTRATION_INFO = [
    { titleKey: 'mo.registrationInfo', descKey: 'mo.registrationInfoDesc' },
    { titleKey: 'mo.competitionTimed', descKey: 'mo.competitionTimedDesc' },
    { titleKey: 'mo.hikingClass', descKey: 'mo.hikingClassDesc' }
  ];
  const [event, setEvent] = useState<Event | null>(null);
  const [previousEdition, setPreviousEdition] = useState<EventEdition | null>(null);
  const [nextEdition, setNextEdition] = useState<EventEdition | null>(null);
  const [previousEditionStatus, setPreviousEditionStatus] = useState<string>('');
  const [nextEditionStatus, setNextEditionStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [userRegistration, setUserRegistration] = useState<any | null>(null);
  const [activeParticipants, setActiveParticipants] = useState<number>(0);
  const [waitingListCount, setWaitingListCount] = useState<number>(0);
  const [isLoadingCounts, setIsLoadingCounts] = useState(true);

  const isRegistrationInvalid = userRegistration?.status === 'cancelled' || userRegistration?.status === 'expired';
  const hasActiveRegistration = Boolean(userRegistration && !isRegistrationInvalid);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUserId(u?.uid || null);
    });
    return unsub;
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadMyReg = async () => {
      if (!userId || !nextEdition?.id) {
        if (isMounted) setUserRegistration(null);
        return;
      }
      try {
        const regs = await getRegistrationsByUserId(userId, nextEdition.id);
        if (!isMounted) return;
        setUserRegistration((regs || []).length > 0 ? regs[0] : null);
      } catch {
        if (isMounted) setUserRegistration(null);
      }
    };
    loadMyReg();
    return () => {
      isMounted = false;
    };
  }, [nextEdition?.id, userId]);

  useEffect(() => {
    let isMounted = true;
    const loadLogo = async () => {
      try {
        const url = await getEventLogoUrl(EVENT_ID);
        if (isMounted) setLogoUrl(url);
      } catch {
        if (isMounted) setLogoUrl(null);
      }
    };
    loadLogo();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const eventData = await getEvent(EVENT_ID);
        setEvent(eventData);

        const { previous, next } = await getAdjacentEditions(EVENT_ID);

        if (previous) {
          setPreviousEdition(previous);
          const statusLabel = await getVerboseName('eventEditions', 'status', previous.status, previous.status);
          setPreviousEditionStatus(statusLabel);
        }

        if (next) {
          setNextEdition(next);
          const statusLabel = await getVerboseName('eventEditions', 'status', next.status, next.status);
          setNextEditionStatus(statusLabel);
        }
      } catch (err: any) {
        console.error('Failed to load MO info:', err);
        setError(err?.message || t('errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [t]);

  // Fetch participant counts for next edition
  useEffect(() => {
    const fetchCounts = async () => {
      if (!nextEdition?.id) {
        setIsLoadingCounts(false);
        return;
      }
      
      try {
        const [activeCount, wlCount] = await Promise.all([
          countActiveParticipants(nextEdition.id),
          countWaitingList(nextEdition.id)
        ]);
        setActiveParticipants(activeCount);
        setWaitingListCount(wlCount);
      } catch (error) {
        console.error('Error fetching participant counts:', error);
      } finally {
        setIsLoadingCounts(false);
      }
    };

    fetchCounts();
  }, [nextEdition?.id]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
        <Box mt={2}>
          <Button variant="contained" onClick={() => navigate('/')}>{t('errors.backToHome')}</Button>
        </Box>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning">{t('errors.notFound')}</Alert>
      </Container>
    );
  }

  // Dynamic check using RH_URL field from Firestore
  const previousEditionHasPage = previousEdition ? Boolean((previousEdition as any).RH_URL) : false;
  const nextEditionHasPage = nextEdition ? Boolean((nextEdition as any).RH_URL) : false;

  const isRegistrationOpenForEdition = (edition: EventEdition): boolean => {
    const now = new Date();
    const statusValue = String((edition as any).status ?? '').toLowerCase();
    const statusNumeric = parseInt(statusValue, 10);
    const STATUS_MAP: Record<string, number> = {
      hidden: 0, draft: 10, announced: 20, pre_registration: 30, open: 40,
      waitlist: 44, late_registration: 50, full: 54, closed: 60,
      in_progress: 70, suspended: 75, finished: 80, cancelled: 90, finalized: 100
    };
    const eventStatusCode = !isNaN(statusNumeric) ? statusNumeric : (STATUS_MAP[statusValue] ?? 0);

    const start = (edition.startTime as any)?.toDate ? (edition.startTime as any).toDate() : new Date(edition.startTime as any);
    const raceStarted = now >= start;

    const deadline = (edition.registrationDeadline as any)?.toDate
      ? (edition.registrationDeadline as any).toDate()
      : (edition.registrationDeadline ? new Date(edition.registrationDeadline as any) : null);
    const registrationDeadlinePassed = deadline ? now >= deadline : true;

    const opens = (edition.registrationOpens as any)?.toDate
      ? (edition.registrationOpens as any).toDate()
      : (edition.registrationOpens ? new Date(edition.registrationOpens as any) : null);
    const registrationOpensInFuture = Boolean(opens && now < opens);

    const isRegistrationPhase = [30, 40, 44, 50].includes(eventStatusCode);
    return isRegistrationPhase && !registrationDeadlinePassed && !raceStarted && !registrationOpensInFuture;
  };

  // Logic for showing participants list (same as MO 2026 page)
  const showParticipantsList = nextEdition && (() => {
    const now = new Date();
    const statusValue = String((nextEdition as any).status ?? '').toLowerCase();
    const statusNumeric = parseInt(statusValue, 10);
    const STATUS_MAP: Record<string, number> = {
      hidden: 0, draft: 10, announced: 20, pre_registration: 30, open: 40,
      waitlist: 44, late_registration: 50, full: 54, closed: 60,
      in_progress: 70, suspended: 75, finished: 80, cancelled: 90, finalized: 100
    };
    const eventStatusCode = !isNaN(statusNumeric) ? statusNumeric : (STATUS_MAP[statusValue] ?? 0);
    return eventStatusCode >= 30 && eventStatusCode < 80;
  })();

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back to Home Navigation */}
      <Button
        startIcon={<ArrowLeft />}
        onClick={() => navigate('/')}
        sx={{ mb: 2 }}
      >
        {t('events.backToHome')}
      </Button>
      <Box textAlign="center" mb={6}>
        <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} flexWrap="wrap" mb={1}>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt="MO logo"
              sx={{ width: 44, height: 44, borderRadius: 1, objectFit: 'cover' }}
            />
          )}
          <Typography variant="h2" component="h1" fontWeight={800} gutterBottom sx={{ mb: 0 }}>
            {event.name || 'Malvikingen Opp'}
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 640, mx: 'auto', mb: 3 }}>
          {t('mo.tagline')}
        </Typography>

        <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap" mb={2}>
          <Button
            variant="contained"
            startIcon={<Globe />}
            href={getKrultraUrl('events/MO')}
          >
            {t('events.officialInfo')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<BarChart3 />}
            onClick={() => navigate('/mo/results')}
          >
            {t('events.results')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<BarChart3 />}
            onClick={() => navigate('/mo/all-time')}
          >
            {t('mo.allTimeLeaderboard')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Trophy />}
            onClick={() => navigate('/mo/records')}
          >
            {t('events.records')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Facebook />}
            href="https://www.facebook.com/groups/146973852042384/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('mo.moOnFacebook')}
          </Button>
        </Box>
      </Box>

      {/* Upcoming Event Section - Prominent at top without frame */}
      {nextEdition && (
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          {/* Emphasized Register Button */}
          <Box sx={{ mb: 3 }}>
            {userRegistration ? (
              <Button
                variant="contained"
                size="large"
                sx={{ 
                  px: 6, 
                  py: 2, 
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: 3
                }}
                onClick={() => {
                  void (async () => {
                    await setSelectedEvent(nextEdition.id);
                    navigate('/register');
                  })();
                }}
              >
                {hasActiveRegistration ? t('events.viewRegistration') : t('events.updateRegistration')}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                color="primary"
                disabled={!isRegistrationOpenForEdition(nextEdition)}
                sx={{ 
                  px: 6, 
                  py: 2, 
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: 3
                }}
                onClick={() => {
                  if (!isRegistrationOpenForEdition(nextEdition)) return;
                  void (async () => {
                    await setSelectedEvent(nextEdition.id);
                    if (userId) {
                      navigate('/register');
                    } else {
                      navigate('/auth?returnTo=/register');
                    }
                  })();
                }}
              >
                {userId ? t('events.registerNow') : t('events.loginToRegister')}
              </Button>
            )}
          </Box>

          {/* Date and Status Info */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            <strong>{t('editions.event')}:</strong> {nextEdition.eventName}
          </Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <strong>{t('editions.date')}:</strong> {formatDate(nextEdition.startTime)}
          </Typography>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="span">
              <strong>{t('status.label')}:</strong>
            </Typography>
            <Chip label={nextEditionStatus} size="medium" color="primary" />
          </Box>

          {/* Go to Edition Button */}
          {nextEditionHasPage ? (
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate((nextEdition as any).RH_URL)}
              sx={{ px: 4, py: 1.5 }}
            >
              {t('editions.goToEdition', { edition: nextEdition.edition })}
            </Button>
          ) : (
            <Button variant="outlined" size="large" disabled sx={{ px: 4, py: 1.5 }}>
              {t('editions.detailsComingSoon')}
            </Button>
          )}

          {/* See Participants Button */}
          {showParticipantsList && (
            <Button
              variant="text"
              size="large"
              onClick={() => {
                void (async () => {
                  await setSelectedEvent(nextEdition.id);
                  navigate('/participants');
                })();
              }}
              sx={{ px: 4, py: 1.5, mt: 1 }}
            >
              {waitingListCount > 0 ? t('events.participantsAndWaitlist') : t('events.seeParticipants')}
            </Button>
          )}
        </Box>
      )}

      {/* Previous Edition Button - without frame */}
      {previousEdition && (
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          {previousEditionHasPage ? (
            <Button
              variant="text"
              onClick={() => navigate((previousEdition as any).RH_URL)}
              sx={{ fontSize: '1rem' }}
            >
              {t('editions.seeEdition', { edition: previousEdition.edition })}
            </Button>
          ) : (
            <Button variant="text" disabled>
              {t('editions.detailsComingSoon')}
            </Button>
          )}
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {t('mo.courseFacts')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2} alignItems="flex-start">
              {COURSE_FACTS.map((fact) => (
                <Box key={fact.titleKey} textAlign="left">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t(fact.titleKey)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(fact.descKey)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {t('mo.registrationInfo')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2} alignItems="flex-start">
              {REGISTRATION_INFO.map((item) => (
                <Box key={item.titleKey} textAlign="left">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t(item.titleKey)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(item.descKey)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Box textAlign="center" sx={{ mt: 6 }}>
        <Typography variant="body2" color="text.secondary">
          {t('common.questions')}{' '}
          <Box
            component="a"
            href="mailto:post@krultra.no"
            sx={{
              color: (theme) => theme.palette.mode === 'dark' ? '#69A9E1' : '#4E82B4',
              textDecoration: 'underline',
              '&:hover': {
                color: (theme) => theme.palette.mode === 'dark' ? '#8BC2F0' : '#41719C',
              }
            }}
          >
            post@krultra.no
          </Box>
        </Typography>
      </Box>
    </Container>
  );
};

export default MOOverviewPage;
